if (!customElements.get('slider-marquee')) {
  class SliderMarquee extends HTMLElement {
    constructor() {
      super();

      this.track = null;
      this.originalItems = [];
      this.setWidth = 0;
      this.pos = 0;
      this.rafId = null;
      this.autoRunning = false;
      this.resumeTimer = null;
      this.lastTimestamp = null;
      this.isDragging = false;
      this.hasDragged = false;
      this.dragStartX = 0;
      this.posAtDragStart = 0;
    }

    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.originalItems = Array.from(this.track.children);
      this.speed = parseFloat(this.dataset.speed) || 96;
      this.boundTick = this.tick.bind(this);

      requestAnimationFrame(() => {
        this.setWidth = this.originalItems.reduce(
          (sum, el) => sum + el.offsetWidth,
          0,
        );
        this.cloneItems();
        this.bindEvents();
        this.startAuto();
      });
    }

    cloneItems() {
      this.originalItems.forEach((el) => {
        const clone = el.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        this.track.appendChild(clone);
        clone.querySelectorAll('img[data-src]').forEach((img) => {
          if (typeof lazySizes !== 'undefined') lazySizes.loader.unveil(img);
        });
      });
    }

    remeasure() {
      this.track.querySelectorAll('[aria-hidden]').forEach((el) => el.remove());
      this.setWidth = this.originalItems.reduce(
        (sum, el) => sum + el.offsetWidth,
        0,
      );
      this.cloneItems();
    }

    tick(timestamp) {
      if (this.lastTimestamp !== null) {
        const elapsed = timestamp - this.lastTimestamp;
        this.pos -= (this.speed / 1000) * elapsed;
        if (this.pos <= -this.setWidth) this.pos += this.setWidth;
        this.track.style.transform = `translateX(${this.pos}px)`;
      }
      this.lastTimestamp = timestamp;
      this.rafId = requestAnimationFrame(this.boundTick);
    }

    startAuto() {
      if (this.autoRunning) return;
      this.autoRunning = true;
      this.rafId = requestAnimationFrame(this.boundTick);
    }

    stopAuto() {
      this.autoRunning = false;
      this.lastTimestamp = null;
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    scheduleResume() {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => this.startAuto(), 0);
    }

    onPointerDown(e) {
      if (e.button !== 0) return;

      this.isDragging = true;
      this.hasDragged = false;
      this.dragPointerId = e.pointerId;
      this.dragStartX = e.clientX;
      this.posAtDragStart = this.pos;
      this.stopAuto();
      clearTimeout(this.resumeTimer);

      e.preventDefault();
    }

    onPointerMove(e) {
      if (!this.isDragging) return;
      const delta = e.clientX - this.dragStartX;

      if (!this.hasDragged && Math.abs(delta) > 4) {
        this.hasDragged = true;
        this.classList.add('dragging');
        this.setPointerCapture(this.dragPointerId);
      }

      if (!this.hasDragged) return;

      this.pos = this.posAtDragStart + delta;
      if (this.pos > 0) this.pos -= this.setWidth;
      if (this.pos < -this.setWidth) this.pos += this.setWidth;
      this.track.style.transform = `translateX(${this.pos}px)`;
    }

    onPointerUp() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.classList.remove('dragging');
      this.scheduleResume();

      if (this.hasDragged) {
        const blockClick = (e) => {
          e.stopPropagation();
          e.preventDefault();
          this.removeEventListener('click', blockClick, true);
        };
        this.addEventListener('click', blockClick, true);
      }
    }

    onPointerCancel() {
      this.isDragging = false;
      this.classList.remove('dragging');
      this.scheduleResume();
    }

    bindEvents() {
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => this.remeasure(), 150);
      });

      this.addEventListener('pointerdown', (e) => this.onPointerDown(e));
      this.addEventListener('pointermove', (e) => this.onPointerMove(e));
      this.addEventListener('pointerup', () => this.onPointerUp());
      this.addEventListener('pointercancel', () => this.onPointerCancel());
    }
  }

  customElements.define('slider-marquee', SliderMarquee);
}
